import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import Nav from "@/components/Nav";
import { toast } from "sonner";
import { TrendingUp, Target, Clock, BadgeInfo, ArrowLeft } from "lucide-react";
export default function AdvancedReport() {
  const { aid } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get(`/assessment/${aid}/advanced-report`); setData(data); }
      catch (e) {
        if (e?.response?.status === 402) { toast.error("Unlock required"); nav(`/results/${aid}`); }
        else setErr(e?.response?.data?.detail || "Unavailable");
      }
    })();
  }, [aid, nav]);

  if (err) return <div><Nav /><div className="max-w-xl mx-auto px-5 py-20 text-slate-400">{err}</div></div>;
  if (!data) return <div><Nav /><div className="max-w-xl mx-auto px-5 py-20 text-slate-400">Loading advanced report…</div></div>;

  const diffRows = Object.entries(data.accuracy_by_difficulty);

  return (
    <div><Nav />
      <div className="max-w-5xl mx-auto px-5 py-8">
        <button onClick={()=>nav(`/results/${aid}`)} className="mb-3 text-sm text-slate-400 hover:text-slate-200 inline-flex items-center gap-1" data-testid="back-results">
          <ArrowLeft className="w-4 h-4" /> Back to results
        </button>
        <div className="uppercase font-mono text-xs tracking-[0.24em] text-[#E0A96D] mb-3">Advanced Cognitive Report</div>
        <h1 className="font-serif text-4xl mb-2" data-testid="advanced-title">Deep view of your performance</h1>
        <p className="text-slate-400 max-w-2xl text-sm">All numbers below are provisional DualQ performance scores. No IQ transformation is applied. Items are EXPERIMENTAL.</p>

        <section className="mt-8 grid gap-4 md:grid-cols-2">
          <Panel icon={Target} color="#38BDF8" title="Accuracy by Difficulty">
            <table className="w-full text-sm" data-testid="accuracy-by-difficulty">
              <thead className="text-xs font-mono text-slate-500 uppercase">
                <tr><th className="text-left py-1">Difficulty</th><th className="text-right">N</th><th className="text-right">Accuracy</th><th className="text-right">Avg time</th></tr>
              </thead>
              <tbody>
                {diffRows.map(([d, r]) => (
                  <tr key={d} className="border-t border-slate-800/60">
                    <td className="py-2">{d}/5</td>
                    <td className="text-right font-mono">{r.n}</td>
                    <td className="text-right font-mono">{r.accuracy_pct != null ? `${r.accuracy_pct}%` : "—"}</td>
                    <td className="text-right font-mono">{r.avg_rt_ms != null ? `${(r.avg_rt_ms/1000).toFixed(1)}s` : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Panel>
          <Panel icon={Clock} color="#F59E0B" title="Speed · Accuracy · Evidence">
            <div className="space-y-2" data-testid="speed-accuracy">
              {data.speed_accuracy.map(x => (
                <div key={x.domain} className="rounded-xl bg-[#0E172A]/60 p-3 border border-slate-800/60">
                  <div className="flex items-center justify-between">
                    <div className="text-sm capitalize text-slate-200">{x.domain.replace(/_/g, " ")}</div>
                    <div className="font-mono text-sm">{x.score}</div>
                  </div>
                  <div className="mt-1 text-[11px] font-mono text-slate-500 flex gap-x-3 flex-wrap">
                    <span>n={x.items_completed}</span>
                    {x.accuracy_pct != null && <span>accuracy {x.accuracy_pct}%</span>}
                    {x.avg_rt_ms != null && <span>~{(x.avg_rt_ms/1000).toFixed(1)}s</span>}
                    <span>evidence {x.evidence_strength}</span>
                  </div>
                </div>
              ))}
            </div>
          </Panel>
        </section>

        <section className="mt-6 grid gap-4 md:grid-cols-2">
          <Panel icon={TrendingUp} color="#10B981" title="Demonstrated strengths">
            <ul className="space-y-2 text-sm text-slate-200" data-testid="strengths-list">
              {data.strengths.map(s => (
                <li key={s.domain} className="flex justify-between">
                  <span className="capitalize">{s.domain.replace(/_/g, " ")}</span>
                  <span className="font-mono text-emerald-300">{s.score}</span>
                </li>
              ))}
            </ul>
          </Panel>
          <Panel icon={TrendingUp} color="#F97316" title="Areas worth exploring">
            <ul className="space-y-2 text-sm text-slate-200" data-testid="development-list">
              {data.development_areas.map(s => (
                <li key={s.domain} className="flex justify-between">
                  <span className="capitalize">{s.domain.replace(/_/g, " ")}</span>
                  <span className="font-mono text-amber-300">{s.score}</span>
                </li>
              ))}
            </ul>
          </Panel>
        </section>

        <section className="mt-6">
          <Panel icon={BadgeInfo} color="#A855F7" title="Evidence quality for this profile">
            <div className="grid sm:grid-cols-4 gap-3" data-testid="evidence-summary">
              <Stat label="High evidence" value={data.evidence_summary.HIGH} color="#10B981" />
              <Stat label="Moderate" value={data.evidence_summary.MODERATE} color="#F59E0B" />
              <Stat label="Low evidence" value={data.evidence_summary.LOW} color="#64748B" />
              <Stat label="Total domains" value={data.evidence_summary.total_domains} color="#38BDF8" />
            </div>
          </Panel>
        </section>

        <section className="mt-6">
          <Panel icon={BadgeInfo} color="#E0A96D" title="IQ estimate status">
            <div className="text-sm text-slate-300 space-y-2" data-testid="iq-status">
              <div className="font-serif text-xl text-amber-200" data-testid="iq-headline">{data.iq_estimate.headline || "IQ Estimate Not Yet Available — Validation in Progress"}</div>
              <p className="text-slate-400 leading-relaxed">{data.iq_estimate.reason}</p>
              <div className="mt-2 pt-2 border-t border-slate-800/60">
                <div className="text-xs font-mono text-slate-500 uppercase mb-1">Provisional cognitive performance</div>
                <div className="font-serif text-3xl">{data.iq_estimate.provisional_cognitive_performance}</div>
              </div>
              <div className="mt-3 pt-3 border-t border-slate-800/60">
                <div className="text-xs font-mono text-slate-500 uppercase mb-2">How this score is calculated</div>
                <ul className="list-disc list-inside text-xs text-slate-400 space-y-1">
                  {data.iq_estimate.methodology_notes.map((n, i) => <li key={i}>{n}</li>)}
                </ul>
              </div>
              {data.assessment_date && (
                <div className="pt-3 border-t border-slate-800/60 text-xs font-mono text-slate-500">
                  Assessment taken: {new Date(data.assessment_date).toLocaleString()}
                </div>
              )}
            </div>
          </Panel>
        </section>

        {data.domain_explanations && (
          <section className="mt-6">
            <Panel icon={BadgeInfo} color="#38BDF8" title="What each domain measures">
              <div className="grid sm:grid-cols-2 gap-3" data-testid="domain-explanations">
                {Object.entries(data.domain_explanations).map(([k, v]) => (
                  <div key={k} className="rounded-xl bg-[#0E172A]/60 p-3 border border-slate-800/60">
                    <div className="text-sm text-slate-200 font-medium">{v.label}</div>
                    <div className="text-xs text-slate-400 mt-1">{v.desc}</div>
                  </div>
                ))}
              </div>
            </Panel>
          </section>
        )}

        <section className="mt-6 flex flex-wrap gap-3">
          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/assessment/${aid}/report.pdf`}
             target="_blank" rel="noreferrer"
             onClick={(e) => {
               e.preventDefault();
               const token = localStorage.getItem("dualq_token");
               fetch(`${process.env.REACT_APP_BACKEND_URL}/api/assessment/${aid}/report.pdf`, {
                 headers: { Authorization: `Bearer ${token}` }
               }).then(r => r.blob()).then(b => {
                 const u = URL.createObjectURL(b);
                 const a = document.createElement("a"); a.href = u; a.download = `dualq-advanced-${aid.slice(0,8)}.pdf`; a.click();
                 URL.revokeObjectURL(u);
                 toast.success("PDF downloaded");
               }).catch(() => toast.error("PDF download failed"));
             }}
             className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-[#E0A96D] text-[#070C18] font-semibold hover:bg-[#F0C28B]"
             data-testid="download-pdf-btn">
            Download PDF
          </a>
        </section>
      </div>
    </div>
  );
}

function Panel({ icon:Icon, color, title, children }) {
  return (
    <div className="rounded-2xl bg-[#131E36] border border-slate-800/70 p-5 sm:p-6">
      <div className="flex items-center gap-2 mb-4 uppercase font-mono text-xs tracking-widest" style={{ color }}>
        <Icon className="w-4 h-4" /> {title}
      </div>
      {children}
    </div>
  );
}
function Stat({ label, value, color }) {
  return (
    <div className="rounded-xl bg-[#0E172A] p-3 border border-slate-800/60">
      <div className="text-[10px] uppercase font-mono tracking-widest" style={{ color }}>{label}</div>
      <div className="font-serif text-2xl mt-1">{value}</div>
    </div>
  );
}
